import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

// Componente de Calendario Personalizado
interface CustomCalendarProps {
  selectedDate: string;
  onDateSelect: (date: string) => void;
  professionalSchedule: any[];
  specialDayDates?: string[];
  onlySpecialDays?: boolean;
  minDate?: string;
  maxDate?: string;
}

const CustomCalendar: React.FC<CustomCalendarProps> = ({
  selectedDate,
  onDateSelect,
  professionalSchedule,
  specialDayDates = [],
  onlySpecialDays = false,
  minDate,
  maxDate
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const isDateAvailable = (date: Date): boolean => {
    const dateStr = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
    // Día especial siempre disponible
    if (specialDayDates.includes(dateStr)) return true;
    // Si solo días especiales, el resto no disponible
    if (onlySpecialDays) return false;

    const dayOfWeek = date.getDay();
    const daySchedule = professionalSchedule.find(day => {
      const dayMapping = {
        'sunday': 0, 'monday': 1, 'tuesday': 2, 'wednesday': 3,
        'thursday': 4, 'friday': 5, 'saturday': 6
      };
      return dayMapping[day.day as keyof typeof dayMapping] === dayOfWeek;
    });
    return daySchedule ? daySchedule.isWorking : false;
  };

  const isPastDate = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const isInRange = (date: Date): boolean => {
    const dateStr = date.toISOString().split('T')[0];
    const min = minDate || new Date().toISOString().split('T')[0];
    const max = maxDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    return dateStr >= min && dateStr <= max;
  };

  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    const currentDate = new Date(startDate);

    for (let i = 0; i < 42; i++) {
      const date = new Date(currentDate);
      const isCurrentMonth = date.getMonth() === month;
      const isAvailable = isCurrentMonth && isDateAvailable(date) && !isPastDate(date) && isInRange(date);
      const isPast = isPastDate(date);
      const dateStr = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
      const isSelected = dateStr === selectedDate;
      const isToday = date.toDateString() === new Date().toDateString();

      days.push({
        date: new Date(date),
        day: date.getDate(),
        isCurrentMonth,
        isAvailable,
        isPast,
        isSelected,
        isToday
      });

      currentDate.setDate(currentDate.getDate() + 1);
    }

    return days;
  };

  const days = generateCalendarDays();

  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const handleDayClick = (dayInfo: any) => {
    if (dayInfo.isAvailable) {
      const dateStr = dayInfo.date.toISOString().split('T')[0];
      onDateSelect(dateStr);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={previousMonth}
          className="p-1 hover:bg-gray-100 rounded transition-colors"
        >
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <h3 className="text-lg font-semibold text-gray-900">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h3>

        <button
          type="button"
          onClick={nextMonth}
          className="p-1 hover:bg-gray-100 rounded transition-colors"
        >
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-2">
        {dayNames.map(day => (
          <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {days.map((dayInfo, index) => {
          let dayClasses = "w-8 h-8 flex items-center justify-center text-sm rounded-md cursor-pointer transition-all duration-200 ";

          if (!dayInfo.isCurrentMonth) {
            dayClasses += "text-gray-300 cursor-not-allowed ";
          } else if (dayInfo.isPast) {
            dayClasses += "text-gray-400 cursor-not-allowed bg-gray-50 ";
          } else if (dayInfo.isAvailable) {
            if (dayInfo.isSelected) {
              dayClasses += "bg-blue-600 text-white font-semibold shadow-md ";
            } else {
              dayClasses += "bg-green-100 text-green-800 hover:bg-green-200 font-medium ";
            }
          } else {
            dayClasses += "text-gray-400 bg-red-50 cursor-not-allowed ";
          }

          if (dayInfo.isToday && !dayInfo.isSelected) {
            dayClasses += "ring-2 ring-blue-300 ";
          }

          return (
            <button
              key={index}
              type="button"
              onClick={() => handleDayClick(dayInfo)}
              disabled={!dayInfo.isAvailable || !dayInfo.isCurrentMonth}
              className={dayClasses}
            >
              {dayInfo.day}
            </button>
          );
        })}
      </div>

      <div className="mt-4 flex items-center justify-center space-x-4 text-xs">
        <div className="flex items-center">
          <div className="w-3 h-3 bg-green-100 rounded mr-1"></div>
          <span className="text-gray-600">Disponible</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-red-50 rounded mr-1"></div>
          <span className="text-gray-600">No disponible</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-blue-600 rounded mr-1"></div>
          <span className="text-gray-600">Seleccionado</span>
        </div>
      </div>
    </div>
  );
};

// Interface para las citas
interface Appointment {
  id: number;
  professional_id: number;
  service_name?: string;
  appointment_date: string;
  appointment_time: string;
  duration: number;
  price: number;
  professional_name?: string;
  professional_especialidad?: string;
  client_name?: string;
  status: string;
  location_id?: number;
}

// Props del modal
interface RescheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  appointment: Appointment | null;
  onSuccess: (newDate: string, newTime: string) => void;
  skipValidation?: boolean; // Para profesionales: saltar restricción de 48hs
}

// ===================================================================
// VALIDACIÓN: Verificar si el turno puede reagendarse (48hs de anticipación)
// ===================================================================
const canRescheduleAppointment = (appointmentDate: string, appointmentTime: string): { 
  canReschedule: boolean; 
  hoursUntilAppointment: number;
  message?: string;
} => {
  try {
    // Crear fecha del turno
    const [year, month, day] = appointmentDate.split('-').map(Number);
    const [hours, minutes] = appointmentTime.split(':').map(Number);
    const appointmentDateTime = new Date(year, month - 1, day, hours, minutes);
    
    // Fecha actual
    const now = new Date();
    
    // Calcular diferencia en horas
    const diffInMs = appointmentDateTime.getTime() - now.getTime();
    const hoursUntilAppointment = diffInMs / (1000 * 60 * 60);
    
    // Política: 48 horas de anticipación
    const MINIMUM_HOURS_TO_RESCHEDULE = 48;
    
    if (hoursUntilAppointment < 0) {
      return {
        canReschedule: false,
        hoursUntilAppointment: 0,
        message: 'No se puede reagendar un turno que ya pasó'
      };
    }
    
    if (hoursUntilAppointment < MINIMUM_HOURS_TO_RESCHEDULE) {
      return {
        canReschedule: false,
        hoursUntilAppointment: Math.round(hoursUntilAppointment),
        message: `Debe reagendar con al menos 48 horas de anticipación. Tu turno es en ${Math.round(hoursUntilAppointment)} horas.`
      };
    }
    
    return {
      canReschedule: true,
      hoursUntilAppointment: Math.round(hoursUntilAppointment)
    };
  } catch (error) {
    console.error('Error validando fecha de reagendamiento:', error);
    return {
      canReschedule: false,
      hoursUntilAppointment: 0,
      message: 'Error al validar la fecha del turno'
    };
  }
};


const RescheduleModal: React.FC<RescheduleModalProps> = ({
  isOpen,
  onClose,
  appointment,
  onSuccess,
  skipValidation = false
}) => {
  const [loading, setLoading] = useState(false);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [timeSlotsLoading, setTimeSlotsLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [professionalSchedule, setProfessionalSchedule] = useState<any[]>([]);
  const [specialDayDates, setSpecialDayDates] = useState<string[]>([]);
  const [onlySpecialDays, setOnlySpecialDays] = useState(false);
  const [availableTimeSlots, setAvailableTimeSlots] = useState<string[]>([]);
  
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [reason, setReason] = useState('');
  const [canReschedule, setCanReschedule] = useState(true);
  const [rescheduleValidation, setRescheduleValidation] = useState<{
    canReschedule: boolean;
    hoursUntilAppointment: number;
    message?: string;
  } | null>(null);

  useEffect(() => {
    if (isOpen && appointment) {
      if (skipValidation) {
        // Profesional: sin restricción de 48hs
        setRescheduleValidation({ canReschedule: true, hoursUntilAppointment: 999 });
        setCanReschedule(true);
        loadProfessionalSchedule(appointment.professional_id);
      } else {
        // Paciente: validar 48 horas de anticipación
        const validation = canRescheduleAppointment(
          appointment.appointment_date,
          appointment.appointment_time
        );
        setRescheduleValidation(validation);
        setCanReschedule(validation.canReschedule);
        if (validation.canReschedule) {
          loadProfessionalSchedule(appointment.professional_id);
        }
      }

      setSelectedDate('');
      setSelectedTime('');
      setReason('');
      setError('');
    }
  }, [isOpen, appointment]);

  useEffect(() => {
    if (selectedDate && appointment) {
      loadTimeSlotsForDate(appointment.professional_id, selectedDate);
    }
  }, [selectedDate, appointment]);

  const loadProfessionalSchedule = async (professionalId: number) => {
    setScheduleLoading(true);
    try {
      const locationId = appointment?.location_id;
      const locParam = locationId ? `?locationId=${locationId}` : '';

      // Cargar config (horarios + onlySpecialDays)
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/schedule/professional/${professionalId}/config/public${locParam}`);
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.scheduleConfig?.workingDays) {
          setProfessionalSchedule(data.scheduleConfig.workingDays);
          setOnlySpecialDays(data.scheduleConfig.onlySpecialDays || false);
        }
      }

      // Cargar excepciones (días especiales + bloqueados)
      try {
        const excRes = await fetch(`${process.env.REACT_APP_API_URL}/api/schedule/professional/${professionalId}/exceptions/public${locParam}`);
        if (excRes.ok) {
          const excData = await excRes.json();
          if (excData.success && excData.exceptions) {
            const special = excData.exceptions
              .filter((e: any) => e.is_working && e.exception_type === 'custom_hours')
              .map((e: any) => typeof e.exception_date === 'string' ? e.exception_date.split('T')[0] : new Date(e.exception_date).toISOString().split('T')[0]);
            setSpecialDayDates(special);
          }
        }
      } catch (excErr) { console.error('Error cargando excepciones:', excErr); }
    } catch (error) {
      console.error('Error cargando horarios del profesional:', error);
      setProfessionalSchedule([
        { day: 'monday', dayName: 'Lunes', isWorking: true },
        { day: 'tuesday', dayName: 'Martes', isWorking: true },
        { day: 'wednesday', dayName: 'Miércoles', isWorking: true },
        { day: 'thursday', dayName: 'Jueves', isWorking: true },
        { day: 'friday', dayName: 'Viernes', isWorking: true },
        { day: 'saturday', dayName: 'Sábado', isWorking: false },
        { day: 'sunday', dayName: 'Domingo', isWorking: false }
      ]);
    } finally {
      setScheduleLoading(false);
    }
  };

  const loadTimeSlotsForDate = async (professionalId: number, date: string) => {
    setTimeSlotsLoading(true);
    try {
      const locationId = appointment?.location_id;
      const locParam = locationId ? `&locationId=${locationId}` : '';
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/appointments/professional/${professionalId}/available-slots?date=${date}${locParam}`
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.availableSlots) {
          const times = data.availableSlots
            .filter((slot: any) => slot.available)
            .map((slot: any) => slot.time);
          setAvailableTimeSlots(times);
        } else {
          setAvailableTimeSlots([]);
        }
      } else {
        setAvailableTimeSlots([]);
      }
    } catch (error) {
      console.error('Error cargando horarios disponibles:', error);
      setAvailableTimeSlots([]);
    } finally {
      setTimeSlotsLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedDate || !selectedTime) {
      setError('Por favor selecciona una nueva fecha y hora');
      return;
    }

    if (!appointment) return;

    setLoading(true);
    setError('');

    try {
      const userData = localStorage.getItem('user');
      const user = userData ? JSON.parse(userData) : null;
      const token = localStorage.getItem('token');

      if (!user?.id) {
        throw new Error('Usuario no autenticado');
      }

      // Profesional usa su endpoint con auth, paciente usa el público
      const url = skipValidation
        ? `${process.env.REACT_APP_API_URL}/api/appointments/professional/${appointment.professional_id}/appointment/${appointment.id}/reschedule`
        : `${process.env.REACT_APP_API_URL}/api/appointments/${appointment.id}/reschedule`;

      const response = await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
          body: JSON.stringify({
            newDate: selectedDate,
            newTime: selectedTime,
            reason: reason || (skipValidation ? 'Reagendado por profesional' : 'Reagendamiento solicitado por el paciente'),
            patientId: user.id
          })
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        onSuccess(selectedDate, selectedTime); // CAMBIO AQUÍ: pasar los argumentos
        onClose();
      } else {
        setError(data.message || data.error || 'Error al reagendar la cita');
      }
    } catch (error) {
      console.error('Error reagendando cita:', error);
      setError('Error de conexión. Por favor intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const [year, month, day] = dateString.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatPrice = (price: number) => {
    return price.toLocaleString('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    });
  };

  if (!isOpen || !appointment) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end md:items-center justify-center z-[9999] md:p-4">
      <div className="bg-white rounded-t-2xl md:rounded-2xl max-w-4xl w-full max-h-[92vh] md:max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold text-gray-900">Reagendar Cita</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-blue-900 mb-3">Cita actual</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-blue-700 font-medium">Profesional:</span>
                <p className="text-blue-900">{appointment.professional_name}</p>
              </div>
              <div>
                <span className="text-blue-700 font-medium">Servicio:</span>
                <p className="text-blue-900">{appointment.service_name}</p>
              </div>
              <div>
                <span className="text-blue-700 font-medium">Fecha actual:</span>
                <p className="text-blue-900 capitalize">
                  {formatDate(appointment.appointment_date)} - {appointment.appointment_time}
                </p>
              </div>
              <div>
                <span className="text-blue-700 font-medium">Precio:</span>
                <p className="text-blue-900 font-bold">{formatPrice(appointment.price)}</p>
              </div>
            </div>
          </div>

          {/* Advertencia si NO puede reagendar (menos de 48 horas) */}
          {!canReschedule && rescheduleValidation?.message && (
            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-5 mb-6">
              <div className="flex items-start space-x-3">
                <svg className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div className="flex-1">
                  <h4 className="font-bold text-red-900 mb-1">No se puede reagendar</h4>
                  <p className="text-red-800 mb-2">{rescheduleValidation.message}</p>
                  <p className="text-sm text-red-700">
                    📞 Por favor, contacta directamente al profesional para coordinar cualquier cambio.
                  </p>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L5.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <span className="text-red-800">{error}</span>
              </div>
            </div>
          )}

          {/* Formulario de reagendamiento - Solo si puede reagendar */}
          {canReschedule && (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Selecciona una nueva fecha
              </label>
              
              {scheduleLoading ? (
                <div className="bg-gray-50 rounded-lg p-8 text-center">
                  <div className="animate-spin h-6 w-6 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-2"></div>
                  <p className="text-gray-600">Cargando horarios...</p>
                </div>
              ) : (
                <CustomCalendar
                  selectedDate={selectedDate}
                  onDateSelect={setSelectedDate}
                  professionalSchedule={professionalSchedule}
                  specialDayDates={specialDayDates}
                  onlySpecialDays={onlySpecialDays}
                  minDate={new Date().toISOString().split('T')[0]}
                  maxDate={(() => {
                    const maxDate = new Date();
                    maxDate.setDate(maxDate.getDate() + 30);
                    return maxDate.toISOString().split('T')[0];
                  })()}
                />
              )}

              {selectedDate && (
                <div className="mt-3 p-3 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-800 font-medium">
                    Nueva fecha: {formatDate(selectedDate)}
                  </p>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Horarios disponibles
              </label>

              {!selectedDate ? (
                <div className="bg-gray-50 rounded-lg p-8 text-center">
                  <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-gray-600">Selecciona una fecha para ver los horarios disponibles</p>
                </div>
              ) : timeSlotsLoading ? (
                <div className="bg-gray-50 rounded-lg p-8 text-center">
                  <div className="animate-spin h-6 w-6 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-2"></div>
                  <p className="text-gray-600">Cargando horarios...</p>
                </div>
              ) : availableTimeSlots.length > 0 ? (
                <div className="grid grid-cols-3 gap-2">
                  {availableTimeSlots.map((time) => (
                    <button
                      key={time}
                      type="button"
                      onClick={() => setSelectedTime(time)}
                      className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                        selectedTime === time
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-blue-300 hover:bg-blue-50'
                      }`}
                    >
                      {time}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-800">No hay horarios disponibles para esta fecha</p>
                  <p className="text-xs text-red-600 mt-1">Por favor selecciona otra fecha</p>
                </div>
              )}

              {selectedTime && (
                <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800 font-medium">
                    Nueva hora: {selectedTime}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Motivo del reagendamiento (opcional)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Explica brevemente por qué necesitas reagendar..."
            />
          </div>
            </>
          )}

          {/* Botones de acción */}
          <div className="flex space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-100 text-gray-700 px-6 py-3 rounded-xl font-medium hover:bg-gray-200 transition-colors"
            >
              {canReschedule ? 'Cancelar' : 'Cerrar'}
            </button>
            
            {canReschedule && (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading || !selectedDate || !selectedTime}
                className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-xl font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                    Reagendando...
                  </div>
                ) : (
                  'Reagendar Cita'
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RescheduleModal;